/**
 * Career Service
 * Handles job postings, applications, and career-related operations
 */

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  startAfter,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';

/**
 * Helper function to safely convert Firestore timestamps and dates
 */
const convertFirestoreDate = (dateField) => {
  if (!dateField) return null;
  
  try {
    if (dateField instanceof Timestamp) {
      return dateField.toDate();
    } else if (typeof dateField === 'string' || typeof dateField === 'number') {
      const date = new Date(dateField);
      return isNaN(date.getTime()) ? null : date;
    } else if (dateField instanceof Date) {
      return isNaN(dateField.getTime()) ? null : dateField;
    }
    return null;
  } catch (error) {
    console.error('Error converting date:', error);
    return null;
  }
};

/**
 * Helper function to normalize job data from Firestore
 */
const normalizeJobData = (doc) => {
  const data = doc.data();
  
  return {
    id: doc.id,
    ...data,
    // Convert all date fields properly
    createdAt: convertFirestoreDate(data.createdAt),
    updatedAt: convertFirestoreDate(data.updatedAt),
    applicationDeadline: convertFirestoreDate(data.applicationDeadline),
    startDate: convertFirestoreDate(data.startDate),
    // Ensure company object has proper structure
    company: {
      id: data.company?.id || null,
      name: data.company?.name || 'Unknown Company',
      logo: data.company?.logo || null,
      location: data.company?.location || null,
      industry: data.company?.industry || null,
      description: data.company?.description || null,
      size: data.company?.size || null,
      founded: data.company?.founded || null,
      website: data.company?.website || null,
      ...data.company
    },
    // Ensure salary object has proper structure
    salary: data.salary ? {
      min: data.salary.min || null,
      max: data.salary.max || null,
      currency: data.salary.currency || 'ZAR',
      period: data.salary.period || 'monthly',
      ...data.salary
    } : null,
    // Ensure requirements object has proper structure
    requirements: data.requirements ? {
      education: data.requirements.education || '',
      experience: data.requirements.experience || '',
      skills: Array.isArray(data.requirements.skills) ? data.requirements.skills : [],
      other: data.requirements.other || '',
      ...data.requirements
    } : {
      education: '',
      experience: '',
      skills: [],
      other: ''
    },
    // Ensure benefits is an array
    benefits: Array.isArray(data.benefits) ? data.benefits : [],
    // Ensure tags is an array
    tags: Array.isArray(data.tags) ? data.tags : [],
    // Ensure applicationCount is an array
    applicationCount: Array.isArray(data.applicationCount) ? data.applicationCount : []
  };
};

/**
 * Get all job postings with pagination
 * 
 * @param {number} limitCount - Number of jobs to retrieve
 * @param {Object} lastDoc - Last document for pagination
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Jobs data with pagination info
 */
export const getJobPostings = async (limitCount = 20, lastDoc = null, filters = {}) => {
  try {
    let jobsQuery = query(
      collection(db, 'jobPostings'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );

    // Apply filters
    if (filters.jobType) {
      jobsQuery = query(jobsQuery, where('jobType', '==', filters.jobType));
    }
    
    if (filters.location) {
      jobsQuery = query(jobsQuery, where('location', '==', filters.location));
    }
    
    if (filters.company) {
      jobsQuery = query(jobsQuery, where('company.name', '==', filters.company));
    }
    
    if (filters.experienceLevel) {
      jobsQuery = query(jobsQuery, where('experienceLevel', '==', filters.experienceLevel));
    }

    // Add pagination
    if (lastDoc) {
      jobsQuery = query(jobsQuery, startAfter(lastDoc));
    }
    
    jobsQuery = query(jobsQuery, limit(limitCount));

    const snapshot = await getDocs(jobsQuery);
    const jobs = snapshot.docs.map(doc => normalizeJobData(doc));

    return {
      jobs,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting job postings:', error);
    throw error;
  }
};

/**
 * Get job posting by ID
 * 
 * @param {string} jobId - Job ID
 * @returns {Promise<Object|null>} Job data or null if not found
 */
export const getJobById = async (jobId) => {
  try {
    const jobRef = doc(db, 'jobPostings', jobId);
    const jobSnap = await getDoc(jobRef);
    
    if (jobSnap.exists()) {
      return normalizeJobData(jobSnap);
    }
    return null;
  } catch (error) {
    console.error('Error getting job by ID:', error);
    throw error;
  }
};

/**
 * Create a new job application
 * 
 * @param {Object} applicationData - Application data
 * @returns {Promise<string>} Application ID
 */
export const createJobApplication = async (applicationData) => {
  try {
    const application = {
      ...applicationData,
      status: 'pending',
      appliedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'jobApplications'), application);
    
    // Update job's application count
    const jobRef = doc(db, 'jobPostings', applicationData.jobId);
    await updateDoc(jobRef, {
      applicationCount: arrayUnion(applicationData.userId),
      updatedAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating job application:', error);
    throw error;
  }
};

/**
 * Get user's job applications
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of applications
 */
export const getUserApplications = async (userId) => {
  try {
    const applicationsQuery = query(
      collection(db, 'jobApplications'),
      where('userId', '==', userId),
      orderBy('appliedAt', 'desc')
    );
    
    const snapshot = await getDocs(applicationsQuery);
    const applications = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const appData = doc.data();
        
        // Get job details
        const jobData = await getJobById(appData.jobId);
        
        return {
          id: doc.id,
          ...appData,
          jobDetails: jobData,
          appliedAt: convertFirestoreDate(appData.appliedAt),
          updatedAt: convertFirestoreDate(appData.updatedAt)
        };
      })
    );
    
    return applications;
  } catch (error) {
    console.error('Error getting user applications:', error);
    throw error;
  }
};

/**
 * Update application status
 * 
 * @param {string} applicationId - Application ID
 * @param {string} status - New status
 * @param {string} message - Optional message
 * @returns {Promise<void>}
 */
export const updateApplicationStatus = async (applicationId, status, message = '') => {
  try {
    const applicationRef = doc(db, 'jobApplications', applicationId);
    await updateDoc(applicationRef, {
      status,
      statusMessage: message,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
};

/**
 * Subscribe to job postings
 * 
 * @param {function} callback - Callback function
 * @param {Object} filters - Filter criteria
 * @returns {function} Unsubscribe function
 */
export const subscribeToJobPostings = (callback, filters = {}) => {
  let jobsQuery = query(
    collection(db, 'jobPostings'),
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc'),
    limit(20)
  );

  // Apply filters
  if (filters.jobType) {
    jobsQuery = query(jobsQuery, where('jobType', '==', filters.jobType));
  }

  return onSnapshot(jobsQuery, (snapshot) => {
    const jobs = snapshot.docs.map(doc => normalizeJobData(doc));
    callback(jobs);
  }, (error) => {
    console.error('Error subscribing to job postings:', error);
  });
};

/**
 * Get job categories and counts
 * 
 * @returns {Promise<Array>} Array of categories with counts
 */
export const getJobCategories = async () => {
  try {
    const jobsQuery = query(
      collection(db, 'jobPostings'),
      where('status', '==', 'active')
    );
    
    const snapshot = await getDocs(jobsQuery);
    const categories = {};
    
    snapshot.docs.forEach(doc => {
      const jobType = doc.data().jobType;
      categories[jobType] = (categories[jobType] || 0) + 1;
    });
    
    return Object.entries(categories).map(([category, count]) => ({
      category,
      count
    }));
  } catch (error) {
    console.error('Error getting job categories:', error);
    throw error;
  }
};

/**
 * Search jobs by keywords
 * 
 * @param {string} searchTerm - Search term
 * @param {Object} filters - Additional filters
 * @returns {Promise<Array>} Array of matching jobs
 */
export const searchJobs = async (searchTerm, filters = {}) => {
  try {
    // Note: Firestore doesn't support full-text search natively
    // This is a basic implementation that searches in job titles and descriptions
    const jobsQuery = query(
      collection(db, 'jobPostings'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(jobsQuery);
    const jobs = snapshot.docs
      .map(doc => normalizeJobData(doc))
      .filter(job => {
        const searchTermLower = searchTerm.toLowerCase();
        const titleMatch = job.title?.toLowerCase().includes(searchTermLower);
        const descriptionMatch = job.description?.toLowerCase().includes(searchTermLower);
        const companyMatch = job.company?.name?.toLowerCase().includes(searchTermLower);
        const skillsMatch = job.requirements?.skills?.some(skill => 
          skill.toLowerCase().includes(searchTermLower)
        );
        
        return titleMatch || descriptionMatch || companyMatch || skillsMatch;
      });
    
    return jobs;
  } catch (error) {
    console.error('Error searching jobs:', error);
    throw error;
  }
};

/**
 * Check if user has already applied to a job
 * 
 * @param {string} userId - User ID
 * @param {string} jobId - Job ID
 * @returns {Promise<boolean>} True if already applied
 */
export const hasUserApplied = async (userId, jobId) => {
  try {
    const applicationQuery = query(
      collection(db, 'jobApplications'),
      where('userId', '==', userId),
      where('jobId', '==', jobId),
      limit(1)
    );
    
    const snapshot = await getDocs(applicationQuery);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking if user applied:', error);
    throw error;
  }
};

/**
 * Get companies with active job postings
 * 
 * @returns {Promise<Array>} Array of companies
 */
export const getActiveCompanies = async () => {
  try {
    const jobsQuery = query(
      collection(db, 'jobPostings'),
      where('status', '==', 'active')
    );
    
    const snapshot = await getDocs(jobsQuery);
    const companies = new Map();
    
    snapshot.docs.forEach(doc => {
      const job = normalizeJobData(doc);
      const company = job.company;
      
      if (company && company.name) {
        if (!companies.has(company.name)) {
          companies.set(company.name, {
            ...company,
            jobCount: 1
          });
        } else {
          companies.get(company.name).jobCount++;
        }
      }
    });
    
    return Array.from(companies.values());
  } catch (error) {
    console.error('Error getting active companies:', error);
    throw error;
  }
};

export default {
  getJobPostings,
  getJobById,
  createJobApplication,
  getUserApplications,
  updateApplicationStatus,
  subscribeToJobPostings,
  getJobCategories,
  searchJobs,
  hasUserApplied,
  getActiveCompanies
};